<template>
    <app-layout title="Pick Order">

        <page-header>
            <template #title>Pick Order</template>

            <template #actions>
                <!-- Print button / dropdown -->
                <!-- Retail -->
                <jet-dropdown v-if="!order.is_wholesale" align="right" width="24">
                    <template #trigger>
                        <jet-tertiary-button size="small">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handleRetailPrint('invoices')">Order</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handleRetailPrint('labels')">Label</div>
                    </template>
                </jet-dropdown>

                <!-- Wholesale -->
                <jet-dropdown v-else-if="order.status == 2" align="right" width="48">
                    <template #trigger>
                        <jet-tertiary-button size="small">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('confirmation')">Order Confirmation</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('invoice')">Order Invoice</div>
                    </template>
                </jet-dropdown>

                <jet-tertiary-button v-else type="button" class="py-1.5 sm:py-1 leading-none" @click="handlePrint('confirmation')">
                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                </jet-tertiary-button>

                <jet-tertiary-button type="button" class="leading-none" @click="backUrl">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <div
            v-if="mode == 'summary'"
            class="px-3 md:px-4 lg:px-8 gap-y-4"
        >
            <div class="flex flex-col">
                <order-details :order="order" :key="order.status"/>
            </div>

            <div class="flex gap-4 pt-2 pb-4">
                <div>
                    <jet-label for="picker_id" value="Current Picker:" class="w-32 align-top leading-7 whitespace-nowrap" />
                    <div class="flex flex-1 flex-col">
                        <div v-if="picker == null">No Picker Selected</div>
                        <div v-else>{{ picker.name }}</div>

                        <div>
                            <jet-secondary-button type="button" class="leading-none py-1 px-2 mr-2 align-middle" @click="showPickerCode()">
                                Update Picker
                            </jet-secondary-button>
                        </div>
                    </div>
                </div>
            </div>

            
            <div class="py-2">
                <div v-if="!allItemsPicked" class="my-4" >
                    <jet-button class="leading-none whitespace-nowrap" @click="startPicking()" >
                        {{ pickingStarted ? 'Continue' : 'Start' }} Picking
                    </jet-button>
                </div>

                <div v-else-if="!order.picked_at" class="my-4" >
                    <jet-button class="leading-none" :disabled="!allItemPickedAndHasQuantity" @click="markOrderPicked">
                        Complete Picking
                    </jet-button>
                </div>
            </div>
            
            <div class="rounded-t overflow-hidden">
                <table class="table w-full mb-6">
                    <thead>
                        <tr class="align-bottom bg-gray-light border">
                            <th class="px-2 py-1 text-center"></th>
                            <th class="px-2 py-1 text-left">Zone</th>
                            <th class="px-2 py-1 text-left">Product</th>
                            <th class="px-2 py-1 text-left">Picker</th>
                            <th class="px-2 py-1 text-right">Ordered</th>
                            <th class="px-2 py-1 text-right">Picked</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in order_items"
                            :key="item.id"
                            :class="{
                                'item_picked': item.picking_status == 'picked',
                                'item_shorted': item.picking_status == 'shorted',
                            }"
                            class="striped"
                        >
                            <td class="px-2 py-1 text-center" width="56px;">
                                <img src="/images/icons/green_check.svg" v-if="item.picking_status == 'picked' || (item.picking_status == 'shorted' && item.auth_code_id !== null)" width="24" height="24" class="mx-auto" />
                            </td>
                            <td class="px-2 py-1">{{ item.picking_zone }}<span v-if="item.picking_subzone">.{{ item.picking_subzone }}</span></td>
                            <td v-if="order.is_wholesale" class="px-2 py-1">
                                <div v-if="item.parent_id" class="text-xs italic">
                                    Part of "{{ item.parent_item.title }}
                                    <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                    <span v-if="item.parent_item.size"> - {{ item.parent_item.size }}</span>"
                                </div>
                                <div>
                                    {{ item.title }}
                                    <span v-if="item.description"> - {{ item.description }}</span>
                                    <span v-if="item.size"> - {{ item.size }}</span>
                                </div>
                            </td>
                            <td v-else class="px-2 py-1">
                                <div v-if="item.parent_id" class="text-xs italic">
                                    Part of "{{ item.parent_item.title }}
                                    <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                    <span v-if="item.parent_item.description_alt"> - {{ item.parent_item.description_alt }}</span>"
                                </div>
                                <div>
                                    {{ item.title }}
                                    <span v-if="item.description"> - {{ item.description }}</span>
                                    <span v-if="item.description_alt"> - {{ item.description_alt }}</span>
                                </div>
                            </td>
                            <td class="px-2 py-1 text-left">{{ item.picker?.name }}</td>
                            <td class="px-2 py-1 text-right">{{ item.orig_quantity }}</td>
                            <td class="px-2 py-1 text-right">{{ item.picked_quantity }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            v-else-if="mode == 'picking'"
            class="px-3 md:px-4 lg:px-8 gap-y-4"
        >
            <div class="flex items-end gap-x-20 mb-8">
                <jet-tertiary-button class="leading-none" @click="$inertia.visit($page.url)">
                    Pause Picking
                </jet-tertiary-button>

                <div>
                    <div class="font-bold">
                        Zones:
                    </div>
                    <div class="flex gap-x-1">
                        <div
                            v-for="(items, zone) in itemsGroupedByZone"
                            :key="zone"                             
                        >
                            <jet-secondary-button
                                v-if="selectedZone == zone.toString()"
                                class="leading-none mr-3"
                            >
                                {{ zone == '0' ? 'No Zone' : zone }}
                            </jet-secondary-button>
                            <jet-button 
                                v-else-if="isZoneFullyPicked(zone)"
                                class="leading-none mr-3"
                                @click="selectedZone = zone.toString()"
                            >
                                {{ zone == '0' ? 'No Zone' : zone }}
                            </jet-button>

                            <jet-tertiary-button
                                v-else
                                class="leading-none mr-3"
                                @click="selectedZone = zone.toString()"
                            >
                                {{ zone == '0' ? 'No Zone' : zone }}
                            </jet-tertiary-button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
                <!-- Item -->
                <div
                    v-for="item in itemsGroupedByZone[selectedZone]"
                    :key="item.id"
                    style="height: 40vh;"
                >
                    <!-- Title -->
                    <div>
                        <!-- Wholesale Title -->
                        <template v-if="order.is_wholesale">
                            <div v-if="item.parent_id" class="text-2xl italic text-red-600">
                                Part of "{{ item.parent_item.title }}
                                <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                <span v-if="item.parent_item.size"> - {{ item.parent_item.size }}</span>"
                            </div>

                            <div class="text-2xl font-bold">
                                {{ item.title }}
                                <span v-if="item.description"> - {{ item.description }}</span>
                                <span v-if="item.size"> - {{ item.size }}</span>
                            </div>

                            <div v-if="item.product_notes" class="text-lg font-medium">
                                {{ item.product_notes }}
                            </div>
                        </template>
                        <!-- Retail Title -->
                        <template v-else>
                            <div v-if="item.parent_id" class="text-2xl italic text-red-600">
                                Part of "{{ item.parent_item.title }}
                                <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                <span v-if="item.parent_item.description_alt"> - {{ item.parent_item.description_alt }}</span>"
                            </div>
                            
                            <div class="text-2xl font-bold">
                                {{ item.title }}
                                <span v-if="item.description"> - {{ item.description }}</span>
                            </div>

                            <div v-if="item.description_alt" class="text-lg font-medium">
                                {{ item.description_alt }}
                            </div>
                        </template>
                    </div>

                    <div class="flex gap-x-12">
                        <div class="flex-shrink">
                            <!-- Product Details -->
                            <div>
                                <!-- Wholesale -->
                                <template v-if="order.is_wholesale">
                                    <div v-if="item.picking_zone" class="text-lg font-bold">
                                        Zone {{ item.picking_zone }}<span v-if="item.picking_subzone">.{{ item.picking_subzone }}</span>
                                    </div>
                                </template>
                                <!-- Retail -->
                                <template v-else>
                                    <div v-if="item.picking_zone" class="text-lg font-bold">
                                        Zone {{ item.picking_zone }}<span v-if="item.picking_subzone">.{{ item.picking_subzone }}</span>
                                    </div>
                                </template>
                            </div>

                            <div class="text-2xl font-bold mt-4"
                                :class="{
                                    'text-green-500': item.picking_status === 'picked',
                                    'text-yellow-500': item.picking_status === 'shorted'
                                }"
                            >
                                {{ item.picked_quantity ?? 0 }} of {{ item.orig_quantity }}
                            </div>

                            <div class="mt-6">
                                <jet-button
                                    v-if="!pickOneByOne || (item.picked_quantity ?? 0) + 1 >= (item.orig_quantity ?? 0)" 
                                    class="text-2xl px-6 py-3"
                                    @click="updateOrderItem(item, 'picked')" 
                                    :disabled="updatingOrderItem || item.picking_status !== null"
                                >
                                    Picked
                                </jet-button>
                                <jet-button
                                    v-else
                                    class="text-2xl px-6 py-3"
                                    @click="updatePickedQuantityByOne(item.id)"
                                    :disabled="item.picking_status !== null"
                                >
                                    Picked
                                </jet-button>
                            </div>

                            <div class="mt-6">
                                <jet-tertiary-button
                                    class="text-2xl px-6 py-3"
                                    @click="handleShort(item)"
                                    :disabled="item.picking_status !== null"
                                >
                                    Short
                                </jet-tertiary-button>
                            </div>

                            <div v-if="updatingOrderItem && selectedItem.id === item.id" class="flex gap-x-1 text-sm mx-6">
                                <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                                <div class="pt-1">
                                    Saving...
                                </div>
                            </div>

                            <div v-if="errorMessage" class="text-red-500 mx-6 text-xl">
                                {{ errorMessage }}
                            </div>

                        </div>

                        <div class="flex-grow">
                            <img
                                v-show="item.product.image"
                                :src="'/storage/' + item.product.image"
                                :key="item.product.image"
                                alt="Product Preview"
                                style="width: auto; height: calc(40vh - 5.25rem); object-fit: contain;"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Picker Login -->
        <jet-dialog-modal :show="showPickerCodeModal" @close="cancelPickerCode">
            <template #title>
                    <h3 class="text-2xl">Update Picker</h3>
            </template>

            <template #content>
                <form @submit.prevent="updatePicker">
                    <div class="mb-2">
                        <jet-label value="Code:" class="mb-0" />
                        <jet-input v-model="pickerCode" ref="pickerCode" type="text" class="w-full" required />
                        <jet-input-error :message="pickerCodeError" class="m1" />
                    </div>

                    <input type="submit" class="hidden" ref="submitPickerCode" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitPickerCode.click()">
                    Update
                </jet-button>

                <jet-tertiary-button @click="cancelPickerCode">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>
        
        
        <jet-dialog-modal :show="showQuantityModal" @close="closeQuantityModal">
            <template #title>
                    <h3 class="text-2xl">Picked Quantity</h3>
            </template>

            <template #content>
                <form @submit.prevent="shortOrderItem">
                    <div>
                        <jet-label value="Picked Quantity:" class="mb-0" />
                        <jet-input v-model.number="quantity" type="number" class="w-full" required step="0.5" min="0" :max="selectedItem.quantity" />
                    </div>

                    <input type="submit" class="hidden" ref="submitNewQuantity" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitNewQuantity.click()">
                    Save
                </jet-button>

                <jet-tertiary-button @click="closeQuantityModal">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

    </app-layout>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDropdown from '@/Jetstream/Dropdown';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import OrderDetails from '@/Shared/Orders/OrderDetails';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

import axios from 'axios';

export default {
    props: ['order', 'order_items', 'origPicker'],

    setup(props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        return { isAdmin }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetDropdown,
        JetTertiaryButton,
        OrderDetails,
        JetLabel,
        JetInputError,
        Multiselect,
    },

    data() {
        return {
            mode: 'summary',

            showQuantityModal: false,
            quantity: null,
            selectedItem: null,

            picker: this.origPicker,
            showPickerCodeModal: false,
            pickerCode: null,
            pickerCodeError: null,
            pickerCodeErrorTimeout: null,
            
            pickOneByOne: !this.order.is_wholesale,

            updatingOrderItem: false,
            errorMessage: null,

            itemsGroupedByZone: this.getItemsGroupedByZone(),

            selectedZone: null,
        };
    },

    methods: {
        backUrl () {
            this.$inertia.visit(route('loads.show', { load: this.order.load_order.load_id }));
        },

        updateSelectedZone () {
            if (this.isZoneFullyPicked(this.selectedZone)) this.selectedZone = this.getNextZone();

            if (this.selectedZone === null) this.mode = 'summary';
        },

        updatePickerIfNeeded (response = null, callback = null) {
            if (response === null || response === 'invalid_picker_session')
            {
                this.picker = null;
                this.showPickerCode();
            }
            else if (typeof callback == 'function') callback();
        },

        startPicking() {
            axios.get(`/getPicker`)
            .then((response) => {
                this.updatePickerIfNeeded(response.data, () => {
                    this.mode = 'picking';
                });
            });
        },

        markOrderPicked(){
            axios.post(`/orders/${this.order.id}/markPicked`)
            .then((response) => {
                this.backUrl();        
            });
        },

        updatePickedQuantityByOne(itemId) {
            let item = Object.values(this.itemsGroupedByZone).flat().find(item => item.id === itemId) || null;
            if (!item) return;

            this.selectedItem = item;
            if (this.selectedItem.picked_quantity < this.selectedItem.quantity) {
                this.selectedItem.picked_quantity += 1;
            }
        },

        updateOrderItem(item, status) {
            this.updatingOrderItem = true;
            this.selectedItem = item;

            axios.post(`/order_item/${this.selectedItem.id}/pick`, { status })
            .then((response) => {
                this.updatePickerIfNeeded(response.data, () => {
                    Object.assign(this.selectedItem, response.data);

                    this.updateSelectedZone();
                })
            })
            .catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            })
            .finally(() => {
                this.updatingOrderItem = false;
            });
        },

        shortOrderItem() {
            axios.post(route('orders.items.short.pick', { order_item: this.selectedItem.id, quantity: this.quantity }))
            .then((response) => {
                this.updatePickerIfNeeded(response.data, () => {
                    this.closeQuantityModal();
                    let item = Object.values(this.itemsGroupedByZone).flat().find(item => item.id === response.data.id) || null;
                    if (item) {
                        Object.assign(item, response.data);

                        this.updateSelectedZone();
                    }
                })
            })
            .catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            });
        },

        setSelectedItem(id){
            this.selectedItem = Object.values(this.itemsGroupedByZone).flat().find(item => item.id === id) || null;
        },

        resetSkippedItems () {
            this.order_items.forEach(item => {
                item.skipped = false;
            });
        },

        updatePicker() {
            this.pickerCodeError = null;
            if (this.pickerCodeErrorTimeout) clearTimeout(this.pickerCodeErrorTimeout);

            axios.post(route('orders.update_picker', { picker_code: this.pickerCode }))
            .then((response) => {
                if (response.data == 'invalid_picker_code')
                {
                    this.pickerCodeError = 'Invalid Picker Code';
                    this.pickerCodeErrorTimeout = setTimeout(() => {
                        this.pickerCodeError = null;
                    }, 4 * 1000);
                }
                else
                {
                    this.picker = response.data;
                    this.cancelPickerCode();
                }
            });
        },

        showPickerCode() {
            this.showPickerCodeModal = true;
        },

        cancelPickerCode() {
            this.showPickerCodeModal = false;
            this.pickerCode = null;
        },

        closeQuantityModal() {
            this.showQuantityModal = false;
            this.selectedItem = null;
            this.quantity = null;
        },

        handleShort(item){
            this.setSelectedItem(item.id);

            if(!this.pickOneByOne){
                this.showQuantityModal = true
            }else{
                this.quantity = item.picked_quantity;
                this.shortOrderItem();
            }
        },

        // Printing
        handlePrint(type) {
            window.open((route('orders.print', { orders: this.order.id, printType: type} )), "_blank");
            if (type == 'confirmation') {
                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
            else if (type === 'invoice') {
                this.showMarkShippedAndInvoicedModal = false;

                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
        },

        handleRetailPrint(type) {
            let print_route = 'routific.print.' + type;
            if (this.shouldAutoprint(type)) axios.get(route(print_route, { orders: [this.order.id]} ));
            else window.open((route(print_route, { orders: [this.order.id]} )), "_blank");
            if(type === 'invoices') {
                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
        },

        getItemsGroupedByZone() {
            return this.order_items.reduce((acc, item) => {
                const zone = item.picking_zone ?? '0';
                if (!acc[zone]) {
                    acc[zone] = [];
                }
                acc[zone].push(item);
                return acc;
            }, {});
        },

        isZoneFullyPicked(zoneNumber) {
            return this.itemsGroupedByZone[zoneNumber]?.every(item => item.picking_status !== null);
        },

        getNextZone () {
            let nextZone = undefined;
            
            if (this.selectedZone && this.selectedZone != this.lastZone) nextZone = this.order_items.find(item => item.picking_status === null && item.picking_zone > this.selectedZone);
            else nextZone = this.order_items.find(item => item.picking_status === null);
            
            if (nextZone !== undefined) return nextZone.picking_zone ?? '0';
            else return null;
        },
    },

    computed: {
        lastZone () {
            let zones = Object.keys(this.itemsGroupedByZone);
            return zones[zones.length - 1];
        },

        pickingStarted () {
            return this.order_items.some(item => item.picking_status !== null);
        },

        countItemsUnprocessed() {
            return this.order_items.filter(item => item.picking_status === null).length;
        },

        allItemsPicked() {
            return this.order_items.every((item) => {
                return item.picking_status !== null;
            });
        },

        allItemPickedAndHasQuantity() {
            return this.order_items.every((item) => {
                return item.picking_status !== null && item.picked_quantity !== null;
            });
        },
    },

    watch: {
        mode () {
            this.resetSkippedItems();
        },

        showPickerCodeModal () {
            setTimeout(() => {
                if (this.$refs.pickerCode) {
                    this.$refs.pickerCode.focus();
                }
            }, 100);
        }
    },

    created() {
        let nextZone = this.getNextZone();

        if (nextZone === null) {
            const firstZone = Object.keys(this.itemsGroupedByZone)[0];
            if (firstZone) {
                this.selectedZone = firstZone;
            }
        }
        else this.selectedZone = nextZone;
    }
};
</script>

<style scoped>
    .item_picked {
        @apply bg-green bg-opacity-20 !important;
    }
    .item_shorted {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
</style>